<template>
  <div class="containar">
    <div class="clock-contents-block">
        <div class="bubble-clock-container">
          <div id="bubbleclock" class="clock-previewca bubblefont" :style="bubbleStyle">
            <span class="bubbletime"><p class="bubbleampm">{{ ampm }}</p>{{ hours }}:{{ minutes }}</span>
          </div>
        </div>
        <div class="set">
          <h3>形状設定</h3>
          <div class="shape-controls">
            <button @click="generateRandomShape" class="random-shape-btn">
              <span>ランダムでつくる</span>
            </button>
            <button @click="resetToDefault" class="reset-shape-btn">
              <span>デフォルトにもどす</span>
            </button>
          </div>
          <div class="shape-params">
            <div class="param-group">
              <label class="param-label">
                <span>波の数</span>
                <span class="param-value">{{ Math.round(waves) }}</span>
              </label>
              <input
                type="range"
                v-model.number="waves"
                min="2"
                max="5"
                step="1"
                class="param-slider"
                @input="updateShape"
              />
            </div>
            <div class="param-group">
              <label class="param-label">
                <span>ふわふわ感</span>
                <span class="param-value">{{ softness.toFixed(2) }}</span>
              </label>
              <input 
                type="range" 
                v-model.number="softness" 
                min="0.05" 
                max="0.25" 
                step="0.01"
                class="param-slider"
                @input="updateShape"
              />
            </div>
            <div class="param-group">
              <label class="param-label">
                <span>でかさ</span>
                <span class="param-value">{{ Math.round(size) }}</span>
              </label>
              <input 
                type="range" 
                v-model.number="size" 
                min="130" 
                max="150" 
                step="5"
                class="param-slider"
                @input="updateShape"
              />
            </div>
          </div>
          <h3>色の設定</h3>
          <div class="color-picker-section">
            <div class="color-picker-row">
              <label class="color-picker-label">
                <span>色1</span>
                <input type="color" v-model="customColor1" class="color-picker-input" @input="onCustomColorChange" />
              </label>
              <label class="color-picker-label">
                <span>色2</span>
                <input type="color" v-model="customColor2" class="color-picker-input" @input="onCustomColorChange" />
              </label>
              <label class="color-picker-label" v-if="useThreeColors">
                <span>色3</span>
                <input type="color" v-model="customColor3" class="color-picker-input" @input="onCustomColorChange" />
              </label>
            </div>
            <div class="color-options-row">
              <label class="checkbox-label">
                <input type="checkbox" v-model="useThreeColors" @change="onThreeColorsChange" />
                <span>3色つかう</span>
              </label>
              <label class="angle-label">
                <span>角度</span>
                <input type="number" v-model.number="gradientAngle" min="0" max="360" class="angle-input" @input="onCustomColorChange" />
                <span>°</span>
              </label>
            </div>
          </div>
          <div class="gradient-grid">
            <label class="gradient-option" :class="{ active: color === 'green' }" @click="applyPreset('green', '#0093E9', '#80D0C7', '#a1efc6', true, 256)">
              <input type="radio" value="green" v-model="color" />
              <div class="gradient-preview green-gradient-background"></div>
            </label>
            <label class="gradient-option" :class="{ active: color === 'blue' }" @click="applyPreset('blue', '#FBDA61', '#FF5ACD', '', false, 260)">
              <input type="radio" value="blue" v-model="color" />
              <div class="gradient-preview blue-gradient-background"></div>
            </label>
            <label class="gradient-option" :class="{ active: color === 'red' }" @click="applyPreset('red', '#4158D0', '#FFCC70', '#C850C0', true, 43)">
              <input type="radio" value="red" v-model="color" />
              <div class="gradient-preview red-gradient-background"></div>
            </label>
            <label class="gradient-option" :class="{ active: color === 'sky' }" @click="applyPreset('sky', '#D9AFD9', '#97D9E1', '', false, 260)">
              <input type="radio" value="sky" v-model="color" />
              <div class="gradient-preview sky-gradient-background"></div>
            </label>
            <label class="gradient-option" :class="{ active: color === 'gray' }" @click="applyPreset('gray', '#304352', '#d7d2cc', '', false, 260)">
              <input type="radio" value="gray" v-model="color" />
              <div class="gradient-preview gray-gradient-background"></div>
            </label>
            <label class="gradient-option" :class="{ active: color === 'yellow' }" @click="applyPreset('yellow', '#F3F9A7', '#CAC531', '', false, 260)">
              <input type="radio" value="yellow" v-model="color" />
              <div class="gradient-preview yellow-gradient-background"></div>
            </label>
            <label class="gradient-option" :class="{ active: color === 'lush' }" @click="applyPreset('lush', '#56ab2f', '#a8e063', '', false, 260)">
              <input type="radio" value="lush" v-model="color" />
              <div class="gradient-preview lush-gradient-background"></div>
            </label>
            <label class="gradient-option" :class="{ active: color === 'moon' }" @click="applyPreset('moon', '#4e54c8', '#8f94fb', '', false, 260)">
              <input type="radio" value="moon" v-model="color" />
              <div class="gradient-preview moon-gradient-background"></div>
            </label>
            <label class="gradient-option" :class="{ active: color === 'rose' }" @click="applyPreset('rose', '#f4c4f3', '#fc67fa', '', false, 260)">
              <input type="radio" value="rose" v-model="color" />
              <div class="gradient-preview rose-gradient-background"></div>
            </label>
            <label class="gradient-option" :class="{ active: color === 'steel' }" @click="applyPreset('steel', '#1F1C2C', '#928DAB', '', false, 260)">
              <input type="radio" value="steel" v-model="color" />
              <div class="gradient-preview steel-gradient-background"></div>
            </label>
          </div>
          <h3>配信ソフト用URL</h3>
          <div class="url-description">
            <p>OBSではソースの「ブラウザ」を追加→下記URLを入力すると表示できます。</p>
            <p><span class="attention">黒いアウトラインが出て気になるとき</span>はブラウザソースを右クリック→「ブレンド方法」を「SRGBオフ」にします。</p>
          </div>
          <div class="url-container">
            <input :value="generateUrl()" id="target" type="text" readonly class="url-input" />
            <button @click="copy" class="url-copy-btn">
              <span>コピー</span>
            </button>
          </div>
        </div>
      </div>
  </div>
</template>
<script>
export default {
  name: "app",
  data() {
    const shapeParams = this.getShapeParamsFromQuery();
    return {
      hours: "",
      minutes: "",
      ampm: "",
      color: 'custom',
      urlInput:'https://ruri.info/tool/randombubbleclockdesign',
      waves: shapeParams.waves,
      softness: shapeParams.softness,
      size: shapeParams.size,
      seed: shapeParams.seed,
      clipPath: '',
      animationId: null,
      customColor1: '#0093E9',
      customColor2: '#80D0C7',
      customColor3: '#a1efc6',
      useThreeColors: false,
      gradientAngle: 260,
    };
  },

  computed: {
    gradientBackground: function() {
      if (this.useThreeColors) {
        return `linear-gradient(${this.gradientAngle}deg, ${this.customColor1} 0%, ${this.customColor3} 46%, ${this.customColor2} 100%)`;
      }
      return `linear-gradient(${this.gradientAngle}deg, ${this.customColor1} 0%, ${this.customColor2} 100%)`;
    },
    bubbleStyle: function() {
      return {
        clipPath: this.clipPath,
        background: this.gradientBackground,
        backgroundSize: '180% 180%',
        animation: 'custom-gradient-animation 12s ease infinite'
      };
    },
  },
  mounted: function () {
    this.updateTime();
    this.updateShape();
    setInterval(this.updateTime, 1000);
  },
  methods: {
    getShapeParamsFromQuery: function() {
      // デフォルト値
      const defaults = {
        waves: 3,
        softness: 0.12,
        size: 130,
        seed: 1234
      };
      
      // URLパラメータからshapeを取得
      let shapeParam = this.$route.query.shape;
      
      if (!shapeParam) {
        return defaults;
      }
      
      try {
        // URLデコード
          if (shapeParam.includes('%')) {
            shapeParam = decodeURIComponent(shapeParam);
          }
        
        // Base64デコード
        const decoded = window.atob(shapeParam);
        const params = JSON.parse(decoded);
        
        return {
          waves: params.waves || defaults.waves,
          softness: params.softness !== undefined ? params.softness : defaults.softness,
          size: params.size || defaults.size,
          seed: params.seed || defaults.seed
        };
      } catch (e) {
        console.error('Failed to decode shape parameter:', e);
        return defaults;
      }
    },
    generateBubblePath: function(waves, softness, size, seed) {
      // waves: 波の数
      // softness: 柔らかさ
      // size: ベースの半径
      // seed: ランダムシード
      
      const numWaves = waves || 3;
      const deformation = softness || 0.12;
      const radius = size || 130;
      const centerX = 200; // 要素の中心（400px / 2）
      const centerY = 200; // 要素の中心（400px / 2）
      
      // シンプルな疑似乱数生成器（シードベース）
      let randomSeed = seed || Math.floor(Math.random() * 10000);
      const random = () => {
        randomSeed = (randomSeed * 9301 + 49297) % 233280;
        return randomSeed / 233280;
      };
      
      // 各波形のパラメータを生成（低周波のsin波）
      const waveParams = [];
      for (let i = 0; i < numWaves; i++) {
        waveParams.push({
          frequency: i + 2, // 周波数（2, 3, 4, ...）
          amplitude: (random() * 0.6 + 0.4) * deformation * radius / numWaves, // 振幅
          phase: random() * Math.PI * 2 // 位相
        });
      }
      
      // 点を生成
      const numPoints = 64;
      const pathPoints = [];
      
      for (let i = 0; i < numPoints; i++) {
        const angle = (i / numPoints) * Math.PI * 2;
        let currentRadius = radius;
        
        // 各波形の影響を加算
        for (const wave of waveParams) {
          currentRadius += Math.sin(angle * wave.frequency + wave.phase) * wave.amplitude;
        }
        
        const x = centerX + Math.cos(angle) * currentRadius;
        const y = centerY + Math.sin(angle) * currentRadius;
        
        pathPoints.push({ x, y });
      }
      
      // SVG pathを生成
      let path = `M ${pathPoints[0].x.toFixed(2)} ${pathPoints[0].y.toFixed(2)}`;
      
      for (let i = 0; i < pathPoints.length; i++) {
        const p0 = pathPoints[(i - 1 + pathPoints.length) % pathPoints.length];
        const p1 = pathPoints[i];
        const p2 = pathPoints[(i + 1) % pathPoints.length];
        const p3 = pathPoints[(i + 2) % pathPoints.length];
        
        // Catmull-Rom to Bezier変換
        const tension = 6; // テンションが高いほど滑らか
        const cp1x = p1.x + (p2.x - p0.x) / tension;
        const cp1y = p1.y + (p2.y - p0.y) / tension;
        const cp2x = p2.x - (p3.x - p1.x) / tension;
        const cp2y = p2.y - (p3.y - p1.y) / tension;
        
        path += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
      }
      
      path += ' Z';
      
      return path;
    },
    updateShape: function() {
      const path = this.generateBubblePath(this.waves, this.softness, this.size, this.seed);
      this.clipPath = `path("${path}")`;
    },
    animateToShape: function(targetWaves, targetSoftness, targetSize, targetSeed, duration = 400) {
      // アニメーション中なら停止
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }
      
      const startTime = performance.now();
      const startWaves = this.waves;
      const startSoftness = this.softness;
      const startSize = this.size;
      
      // seedは最初に変更
      this.seed = targetSeed;
      
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // イージング関数
        const eased = 1 - Math.pow(1 - progress, 3);
        
        // 補間
        this.waves = Math.round(startWaves + (targetWaves - startWaves) * eased);
        this.softness = startSoftness + (targetSoftness - startSoftness) * eased;
        this.size = startSize + (targetSize - startSize) * eased;
        
        this.updateShape();
        
        if (progress < 1) {
          this.animationId = requestAnimationFrame(animate);
        } else {
          this.waves = targetWaves;
          this.softness = targetSoftness;
          this.size = targetSize;
          this.updateShape();
          this.animationId = null;
        }
      };
      
      this.animationId = requestAnimationFrame(animate);
    },
    generateRandomShape: function() {
      // ランダムなパラメータを生成
      const newWaves = Math.floor(Math.random() * 4) + 2; // 2-5
      const newSoftness = Math.round((Math.random() * 0.2 + 0.05) * 100) / 100; // 0.05-0.25
      const newSize = Math.floor(Math.random() * 5) * 5 + 130; // 130-150 (5刻み)
      const newSeed = Math.floor(Math.random() * 10000);
      
      // アニメーションで変化
      this.animateToShape(newWaves, newSoftness, newSize, newSeed);
    },
    resetToDefault: function() {
      // アニメーションでデフォルトに戻す
      this.animateToShape(3, 0.12, 130, 1234);
    },
    encodeShapeParams: function() {
      // パラメータをBase64エンコード（カスタムカラーを含む）
      const params = {
        waves: this.waves,
        softness: this.softness,
        size: this.size,
        seed: this.seed,
        color1: this.customColor1,
        color2: this.customColor2,
        color3: this.useThreeColors ? this.customColor3 : '',
        angle: this.gradientAngle
      };
      
      try {
        const json = JSON.stringify(params);
        return window.btoa(json);
      } catch (e) {
        console.error('Failed to encode shape parameters:', e);
        return '';
      }
    },
    generateUrl: function() {
      const shapeParam = this.encodeShapeParams();
      if (shapeParam) {
        return `${this.urlInput}?shape=${encodeURIComponent(shapeParam)}`;
      }
      return this.urlInput;
    },
    applyPreset: function(name, color1, color2, color3, useThree, angle) {
      this.color = name;
      this.customColor1 = color1;
      this.customColor2 = color2;
      this.customColor3 = color3 || '#a1efc6';
      this.useThreeColors = useThree;
      this.gradientAngle = angle;
    },
    onCustomColorChange: function() {
      this.color = 'custom';
    },
    onThreeColorsChange: function() {
      this.color = 'custom';
    },
    updateTime: function () {
      let currentdate = new Date();
      let hours = currentdate.getHours();
      this.ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12;
      hours = hours ? hours : 12; // 0時は12時として扱う
      this.hours = this.zeroPadding(hours, 2);
      this.minutes = this.zeroPadding(currentdate.getMinutes(), 2);
    },
    zeroPadding: function (num, len) {
      let zero = "";

      // 0の文字列を作成
      for (var i = 0; i < len; i++) {
        zero += "0";
      }

      // zeroの文字列と、数字を結合し、後ろ２文字を返す
      return (zero + num).slice(-len);
    },
    copy () {
      if(navigator.clipboard){
        navigator.clipboard.writeText(this.generateUrl())
        console.log('コピー完了')
        return true
      } else {
        return false
      }
    },
  },
};
</script>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@100..900&display=swap');

p {
  margin: 0;
  padding: 0;
  font-size: 14px;
}
.containar{
  margin: 0 auto;
  max-width: 767px;
}

h3{
  font-size: 14px;
  margin-top: 24px;
}

.clock-contents-block{
  text-align: left;
  display: -webkit-flex;
	display: flex;
	-webkit-flex-wrap: nowrap;
	flex-wrap: nowrap;
	align-items: center;
  margin: 24px auto;
  min-width: 0;
  height: 100%;
}

@media screen and (max-width: 630px) {
  .clock-contents-block{
    display: block;
    margin: 0 auto;
  }

  .set{
    margin-top:24px;
    margin-left: 0px!important;
  }
}


#bubbleclock {
  width: 400px;
  height: 400px;
  /* clip-pathは動的に適用される */
  color: #fff;
  white-space: nowrap;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  text-align: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  filter: drop-shadow(0 0 0.5px rgba(255, 255, 255, 0.3));
}

.bubble-clock-container {
  width: 400px;
  height: 400px;
  position: relative;
  flex-shrink: 0;
}


.bubbletime {
  letter-spacing: 0.05em;
  font-size: 62px;
  line-height: 0.75em;
  margin-bottom: 10px;
}

.bubbleampm {
  font-family: "Outfit", sans-serif;
  font-optical-sizing: auto;
  font-weight: 500;
  font-style: normal;
  font-size: 0.5em;
  vertical-align: 2px;
  margin-left: auto;
  margin-right: 0;
  display: flex;
  justify-content: flex-end;
}

.set{
  padding: 8px 12px 24px 12px;
  background-color: #FFFEF9;
  border-radius: 10px;
  -webkit-align-self: flex-start;
	align-self: flex-start;
  box-sizing: border-box;
  margin-left: 24px;
}



.blue-gradient-background {
  background: linear-gradient(260deg,#FBDA61 0%, #FF5ACD 100%);
  background-size: 180% 180%;
  animation: blue-gradient-animation 10s ease infinite;
}

@keyframes blue-gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.red-gradient-background {
  background: linear-gradient(43deg,#4158D0 0%, #C850C0 46%, #FFCC70 100%);
  background-size: 180% 180%;
  animation: red-gradient-animation 6s ease infinite;
}

@keyframes red-gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
.green-gradient-background {
  background: linear-gradient(256deg,#0093E9 0%,#a1efc6 46%, #80D0C7 100%);
  background-size: 240% 240%;
  animation: green-gradient-animation 10s ease infinite;
}

@keyframes green-gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.sky-gradient-background {
  background: linear-gradient(260deg, #D9AFD9 0%, #97D9E1 100%);
  background-size: 240% 240%;
  animation: sky-gradient-animation 12s ease infinite;
}

@keyframes sky-gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.gray-gradient-background {
  background: linear-gradient(260deg, #304352 0%, #d7d2cc 100%);
  background-size: 240% 240%;
  animation: sky-gradient-animation 12s ease infinite;
}

@keyframes gray-gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.yellow-gradient-background {
  background: linear-gradient(260deg, #F3F9A7 0%, #CAC531 100%);
  background-size: 240% 240%;
  animation: yellow-gradient-animation 12s ease infinite;
}

@keyframes yellow-gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.lush-gradient-background {
  background: linear-gradient(260deg, #56ab2f 0%, #a8e063 100%);
  background-size: 240% 240%;
  animation: lush-gradient-animation 12s ease infinite;
}

@keyframes lush-gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.moon-gradient-background {
  background: linear-gradient(260deg, #4e54c8 0%, #8f94fb 100%);
  background-size: 240% 240%;
  animation: moon-gradient-animation 12s ease infinite;
}

@keyframes moon-gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.rose-gradient-background {
  background: linear-gradient(260deg, #f4c4f3 0%, #fc67fa 100%);
  background-size: 240% 240%;
  animation: rose-gradient-animation 12s ease infinite;
}

@keyframes rose-gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.steel-gradient-background {
  background: linear-gradient(260deg, #1F1C2C 0%, #928DAB 100%);
  background-size: 240% 240%;
  animation: steel-gradient-animation 12s ease infinite;
}

@keyframes steel-gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.bubblefont{
  font-family: "Outfit", sans-serif;
  font-optical-sizing: auto;
  font-weight: 500;
  font-style: normal;
}

/* Gradient Selection */
.gradient-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-top: 12px;
}

.gradient-option {
  position: relative;
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 6px;
  transition: all 0.2s ease;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.gradient-option:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.gradient-option.active {
  border-color: #0f172a;
  box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.2);
}

.gradient-option input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.gradient-preview {
  width: 100%;
  height: 32px;
  border-radius: 4px;
  margin: 0;
}

/* Color Picker Section */
.color-picker-section {
  margin-top: 12px;
}

.color-picker-row {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.color-options-row {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #4a5568;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.angle-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #4a5568;
}

.angle-input {
  width: 60px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 13px;
  text-align: center;
}

.color-picker-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #4a5568;
}

.color-picker-input {
  width: 48px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: none;
}

.color-picker-input::-webkit-color-swatch-wrapper {
  padding: 0;
}

.color-picker-input::-webkit-color-swatch {
  border: 2px solid #e2e8f0;
  border-radius: 6px;
}

@keyframes custom-gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

/* URL Section */
.url-description {
  margin-top: 12px;
}

.url-description p {
  color: #4a5568;
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 8px;
}

.attention {
  color: #ff0000;
  font-weight: bold;
}
.url-container {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.url-input {
  flex: 1;
  height: 2.4em;
  padding: 0 12px;
  border-radius: 4px;
  border: none;
  box-shadow: 0 0 0 1px #ccc inset;
  font-size: 13px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  background: #f8f9fa;
}

.url-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px #0f172a;
  background: #fff;
}

.url-copy-btn {
  height: 2.4em;
  padding: 0 16px;
  background: #0f172a;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.url-copy-btn:hover {
  background: #0f172a;
  transform: translateY(-1px);
}

.url-copy-btn:active {
  transform: translateY(0);
}

/* Shape Controls */
.shape-controls {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  margin-bottom: 12px;
}

.random-shape-btn,
.reset-shape-btn {
  flex: 1;
  height: 2.4em;
  padding: 0 16px;
  background: #0f172a;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.random-shape-btn:hover,
.reset-shape-btn:hover {
  background: #1e293b;
  transform: translateY(-1px);
}

.random-shape-btn:active,
.reset-shape-btn:active {
  transform: translateY(0);
}

.reset-shape-btn {
  background: #64748b;
}

.reset-shape-btn:hover {
  background: #475569;
}

/* Shape Parameters */
.shape-params {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.param-group {
  margin-bottom: 16px;
}

.param-group:last-child {
  margin-bottom: 0;
}

.param-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
  color: #4a5568;
}

.param-value {
  font-weight: 600;
  color: #0f172a;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.param-slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #e2e8f0;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}

.param-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #0f172a;
  cursor: pointer;
  transition: all 0.2s ease;
}

.param-slider::-webkit-slider-thumb:hover {
  background: #1e293b;
  transform: scale(1.1);
}

.param-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #0f172a;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.param-slider::-moz-range-thumb:hover {
  background: #1e293b;
  transform: scale(1.1);
}
</style>

